import { type CTAAction, isValidCTAAction } from './BannerConfig';

/**
 * Configuration interface for the Rippling Nav Cross-Sell component.
 * This interface defines the structure of the `nav-xs-configuration` LaunchDarkly
 * flag variation that controls nav cross-sell content and analytics tracking.
 */
export interface RipplingNavCrossSellConfig {
  /**
   * Text displayed on the nav button.
   * Example: "Payroll"
   */
  buttonLabel: string;

  /**
   * Badge text displayed above the logo in the popover dropdown.
   * Example: "Preferred Partner"
   */
  badgeText: string;

  /**
   * Body text displayed in the popover dropdown.
   * Example: "Rippling saves you hours every week on payroll and more..."
   */
  body: string;

  /**
   * Text for the call-to-action button in the popover dropdown.
   * Example: "Book my demo"
   */
  ctaText: string;

  /**
   * Structured CTA action configuration.
   *
   * Supported action types:
   * - 'link': Navigate to a URL or campaign route
   * - 'demo-modal': Open the Rippling Demo Time modal
   */
  ctaAction: CTAAction;

  /**
   * Optional FontIcon name for the nav button icon.
   * Must be a valid icon name from the @wiw/icons library (https://icons.wheniwork.com).
   * Default: "payroll"
   * Example: "payroll", "calendar", "clock"
   */
  buttonIcon?: string;

  /**
   * Optional PostHog event name to capture when the popover is opened.
   * Useful for measuring engagement (open rate vs. CTA click rate).
   */
  phOpenEvent?: string;

  /**
   * Optional PostHog event name to capture when the CTA is clicked.
   */
  phCtaEvent?: string;
}

/**
 * Type guard to validate if a value is a valid RipplingNavCrossSellConfig.
 *
 * @param config - The value to validate
 * @returns True if the config is valid, false otherwise
 */
export function isValidNavCrossSellConfig(config: unknown): config is RipplingNavCrossSellConfig {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return false;
  }

  const obj = config as Record<string, unknown>;

  // Check required string fields
  const requiredStringFields: (keyof RipplingNavCrossSellConfig)[] = ['buttonLabel', 'badgeText', 'body', 'ctaText'];

  for (const field of requiredStringFields) {
    const value = obj[field];
    if (typeof value !== 'string' || value.trim() === '') {
      return false;
    }
  }

  // Check optional string fields
  const optionalStringFields: (keyof RipplingNavCrossSellConfig)[] = ['buttonIcon', 'phOpenEvent', 'phCtaEvent'];

  for (const field of optionalStringFields) {
    const value = obj[field];
    if (value !== undefined && typeof value !== 'string') {
      return false;
    }
  }

  // ctaAction is required and must be valid
  if (!isValidCTAAction(obj.ctaAction)) {
    return false;
  }

  return true;
}
